import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Editor } from '@tiptap/core';
import { useIntl } from 'react-intl';
import { compact, every, isEmpty, last, trim } from 'lodash';
import { IncompleteJsonParser } from 'incomplete-json-parser';
import cuid from 'cuid';

import { useAppDispatch } from 'store';
import { confirmAlert } from 'components/overlay/Confirm';
import { scrollParent } from 'components/utils/scroll';
import { isValidJsonString } from 'components/utils/json';
import { usePhoenixChannel, usePhoenixEvent } from 'utils/hooks/phoenix';
import { SegmentAPI } from 'components/scripts/segment';

import { Scene } from './scene';
import { generate } from './actions';
import i18n from './scene/utils/i18n';

type AITextInputProps = {
  editor: Editor;
  maxLimit: number;
  editorRef: React.RefObject<HTMLDivElement>;
  floatingMenuRef: React.RefObject<HTMLDivElement>;
  promptSuggestions: PromptSuggestion[];
  setAskAIOpen: (state: boolean) => void;
  anchored?: boolean;
  anchorRef?: React.RefObject<HTMLDivElement>;
};

const ANCHORED_MENU_TOP_INSET = 62;
const ANCHORED_MENU_BOTTOM_INSET = 68;
const PROMPT_OPTION_HEIGHT = 40;
const PROMPT_OPTIONS_MAX_HEIGHT = 320;
const PROMPT_INPUT_HEIGHT = 54;

let editorId: string;
const parser = new IncompleteJsonParser();

export const AITextInput = (props: AITextInputProps) => {
  const {
    editorRef,
    floatingMenuRef,
    editor,
    setAskAIOpen,
    anchored = false,
    anchorRef,
    promptSuggestions = [],
    maxLimit = 10,
  } = props;
  const [prompt, setPrompt] = useState<Prompt>({
    value: '',
    short_value: '',
  });
  const [inProgress, setInProgress] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [currentIndex, setIndex] = useState(0);
  const [currentRawContent, setCurrentRawContent] = useState('');
  const [dimensions, setDimensions] = useState({ top: 0, left: 0 });
  const [anchoredLayout, setAnchoredLayout] = useState<{
    position: 'top' | 'bottom';
    maxHeight: number;
  }>({ position: 'bottom', maxHeight: 0 });
  const [isReplacing, setIsReplacing] = useState(false);
  const dispatch = useAppDispatch();
  const [nextPrompt, setNextPrompt] = useState<{
    value?: Prompt;
    status: boolean;
  }>({ status: false });

  const scrollEl = useRef<HTMLDivElement | null>(null);

  const suggestionsCount = suggestions.length;
  const intl = useIntl();

  useEffect(() => {
    editorId = cuid();
  }, []);

  const streamDataChannel = usePhoenixChannel(`stream:${editorId}`);
  usePhoenixEvent({
    channel: streamDataChannel,
    event: 'stream:data',
    callback: ({
      topic_id: topicId,
      stream_text: streamText,
      data,
      is_complete: isComplete,
    }: SuggestionResponse) => {
      let complete = isComplete;
      const isInitialEvent = !!data.response_id && streamText === '';

      if (!isComplete && !isInitialEvent) {
        parser.write(streamText);
      }

      let noteObject: Content;
      if (!isInitialEvent) {
        setCurrentRawContent((prev) => prev + streamText);
        noteObject = parser.getObjects();
      }

      if (topicId === editorId) {
        if (inProgress) {
          setInProgress(false);
        }

        // for cases where openai doesn't send the 'completed' event
        if (currentRawContent.endsWith('}') && !complete) {
          complete = isValidJsonString(currentRawContent);
        }

        setSuggestions((prev) =>
          updateSuggestions(prev, data, noteObject, complete)
        );

        if (complete) {
          parser.reset();
        }
      }
    },
  });

  const updateSuggestions = (
    prev: Suggestion[],
    data: Suggestion,
    noteObject: Content,
    isComplete: boolean = false
  ) =>
    isEmpty(prev)
      ? [{ ...data, content: noteObject, is_complete: false }]
      : prev.map((item, index) =>
          index === prev.length - 1
            ? {
                ...item,
                ...data,
                content: noteObject,
                is_complete: isComplete,
              }
            : item
        );

  const handleGenerateSuggesion = useCallback(
    (customPrompt?: Prompt) => {
      if (!isEmpty(suggestions) && !last(suggestions)?.is_complete) {
        setNextPrompt({ value: customPrompt, status: true });

        return;
      }
      const selection = anchored
        ? editor.getHTML()
        : editor.commands.getSelectedHTML().toString();

      if (
        suggestionsCount === 0 &&
        (isEmpty(trim(selection)) ||
          every([prompt, customPrompt], (selectedOrCustomPrompt) =>
            isEmpty(trim(selectedOrCustomPrompt?.value))
          ))
      ) {
        return;
      }

      setInProgress(true);
      setCurrentRawContent('');
      setPrompt({
        value: '',
        short_value: '',
      });
      setNextPrompt({ status: false });
      parser.reset();
      dispatch(
        generate({
          suggestions,
          selection,
          content: editor.getHTML(),
          prompt: customPrompt || prompt!,
          editor_id: editorId,
        })
      ).catch(() => {
        setInProgress(false);
        setAskAIOpen(false);
      });

      SegmentAPI('track', 'Ask AI request sent', {
        field: editorRef.current?.id,
      });

      setSuggestions((prevSuggestions) =>
        compact([
          ...prevSuggestions,
          !isEmpty(prevSuggestions) && {
            content: {
              modified_selected_part: '',
              modified_full_content: '',
            },
            followup_prompt: '',
            response_id: '',
            is_complete: false,
          },
        ])
      );
    },
    [
      anchored,
      dispatch,
      editor,
      editorRef,
      prompt,
      setAskAIOpen,
      suggestions,
      suggestionsCount,
    ]
  );

  const handleReplaceContent = useCallback(
    (suggestion: Suggestion) => {
      setIsReplacing(true);
      if (suggestion.is_complete) {
        const content = anchored
          ? suggestion?.content?.modified_selected_part
          : suggestion?.content?.modified_full_content;

        editor.commands.setContent(content, true);

        if (anchored) {
          editor.commands.focus('end');
        }

        setAskAIOpen(false);
        setIsReplacing(false);
      }
    },
    [anchored, editor, setAskAIOpen, setIsReplacing]
  );

  const handleOnclose = () => {
    if (!isConfirmOpen && suggestions.length > 0) {
      confirmAlert({
        title: intl.formatMessage(i18n.confirmDiscard),
        confirmLabel: intl.formatMessage(i18n.buttonDiscard),
        cancelLabel: intl.formatMessage(i18n.buttonCancel),
        onConfirm: () => setAskAIOpen(false),
        onCancel: () => setIsConfirmOpen(false),
      });
      setIsConfirmOpen(true);
    }
    if (suggestions.length === 0) {
      setAskAIOpen(false);
    }
  };

  const onDimensionsChange = useCallback(() => {
    if (anchored && anchorRef?.current) {
      const { top, bottom } = anchorRef.current.getBoundingClientRect();
      const spaceAbove = Math.max(0, top - ANCHORED_MENU_TOP_INSET);
      const spaceBelow = Math.max(
        0,
        window.innerHeight - bottom - ANCHORED_MENU_BOTTOM_INSET
      );
      const menuHeight =
        Math.min(
          promptSuggestions.length * PROMPT_OPTION_HEIGHT,
          PROMPT_OPTIONS_MAX_HEIGHT
        ) + PROMPT_INPUT_HEIGHT;
      const position =
        spaceBelow < menuHeight && spaceAbove > spaceBelow ? 'top' : 'bottom';

      setAnchoredLayout({
        position,
        maxHeight: position === 'top' ? spaceAbove : spaceBelow,
      });

      return;
    }

    const topDimension = floatingMenuRef.current?.getBoundingClientRect();
    const leftDimension = editorRef.current?.getBoundingClientRect();

    if (topDimension && leftDimension) {
      const { top, width: floatingMenuWidth } = topDimension;
      const { left } = leftDimension;

      setDimensions({
        top: floatingMenuWidth ? top : window.innerHeight / 2,
        left,
      });
    }
  }, [
    anchored,
    anchorRef,
    promptSuggestions.length,
    floatingMenuRef,
    editorRef,
  ]);

  useEffect(() => {
    if (!anchored) {
      scrollEl.current = scrollParent(floatingMenuRef.current);
      onDimensionsChange();
    }

    return () => {
      if (!anchored) {
        editor.commands.focus();
      }
    };
  }, [anchored, floatingMenuRef, onDimensionsChange, editor]);

  useEffect(() => {
    if (anchored) {
      onDimensionsChange();
      window.addEventListener('scroll', onDimensionsChange, true);
      window.addEventListener('resize', onDimensionsChange);
      const observer = new ResizeObserver(onDimensionsChange);
      if (anchorRef?.current) observer.observe(anchorRef.current);

      return () => {
        window.removeEventListener('scroll', onDimensionsChange, true);
        window.removeEventListener('resize', onDimensionsChange);
        observer.disconnect();
      };
    }

    scrollEl.current = scrollParent(floatingMenuRef.current);
    if (scrollEl.current) {
      scrollEl.current.addEventListener('scroll', onDimensionsChange);
    }

    return () => {
      scrollEl.current?.removeEventListener('scroll', onDimensionsChange);
    };
  }, [anchored, anchorRef, floatingMenuRef, onDimensionsChange]);

  return (
    <Scene
      intl={intl}
      dimensions={dimensions}
      anchored={anchored}
      anchoredPosition={anchoredLayout.position}
      anchoredMaxHeight={anchoredLayout.maxHeight}
      prompt={prompt}
      suggestions={suggestions}
      suggestionsCount={suggestionsCount}
      isReplacing={isReplacing}
      inProgress={inProgress}
      currentIndex={currentIndex}
      maxLimit={maxLimit}
      promptSuggestions={promptSuggestions}
      setPrompt={setPrompt}
      nextPrompt={nextPrompt}
      onGenerate={handleGenerateSuggesion}
      onReplaceContent={handleReplaceContent}
      onClose={handleOnclose}
      setIndex={setIndex}
    />
  );
};
